import { AuthProvider, useSignIn, useSignOut } from "react-auth-kit";
import { useRecoilState, useSetRecoilState } from "recoil";
import { v4 as uuidv4 } from "uuid";
import { signInState } from "./sign-in/SignIn.state";
import getRefreshApi from "./lib/useRefreshApi";
import { AUTH_KEY } from "./consts/auth";
import { useEffect, useRef } from 'react'
import { useFixRefreshTime } from "./hooks/useFixRefreshTime";
import { setAuthController } from "./lib/auth";

const AuthController = () => {
    const signIn = useSignIn();
    const signOut = useSignOut();
    const setSignIn = useSetRecoilState(signInState);

    useEffect(() => {
        setAuthController({ signIn, signOut });
    }, [signIn, signOut]);

    const processedToken = useRef(false);

    useEffect(() => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");
      const refresh_token = params.get("refresh_token");
      const lifespan = params.get("lifespan");
      const emailParam = params.get("email");
      const userId = params.get("user_id");
  
      if (token && refresh_token && !processedToken.current) {
        processedToken.current = true;
        
        // Remove tokens from URL immediately to prevent infinite loops on re-renders
        const newUrl = window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
  
        const { buildSignInToken } = require('./sign-in/SignIn.state');
        const { ETERNAL_REFRESH_TOKEN } = require('./consts/auth');
  
        const authState = buildSignInToken(
          { token, refresh_token, lifespan: Number(lifespan) || 1440 },
          emailParam || userId
        );
  
        localStorage.setItem(ETERNAL_REFRESH_TOKEN, authState?.refreshToken);
  
        if (signIn(authState)) {
          setSignIn(uuidv4());
          window.dispatchEvent(new StorageEvent("auth-storage"));
        }
      }
    }, [signIn, setSignIn]);

    return null; 
}

/**
 * The main application authentication provider.
 * Wraps the `AuthProvider` from `react-auth-kit` and provides a custom `AuthController`
 * to sync authentication state with Recoil and handle token refresh logic.
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The child components to render within the provider.
 * @returns {React.ReactElement} The wrapped application with auth context.
 */
const AppAuthProvider = ({ children }) => {
  // Wrap AuthProvider in a new component
  // so that it can consume recoil states
  const [signInKey, setSignIn] = useRecoilState(signInState);
  const refreshApi = getRefreshApi();

  useFixRefreshTime()

  useEffect(() => {
    const updater = () => {
      if (localStorage.getItem(AUTH_KEY)) {
        setSignIn(uuidv4());
      }
    };

    window.addEventListener("auth-storage", updater);
    return () => {
      window.removeEventListener("auth-storage", updater);
    };
  }, [setSignIn]);

  return (
    <AuthProvider
      key={signInKey}
      authType="localstorage"
      authName={AUTH_KEY}
      refresh={refreshApi}
    >
      <AuthController />
      {children}
    </AuthProvider>
  );
};

export default AppAuthProvider;
